import { Droplets, Receipt, Ticket, Wallet } from 'lucide-react';

import { Alert } from '@/components/ui/alert';
import { SectionCard } from '@/components/ui/card';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { selectUser, useAuthStore } from '@/features/auth/auth.store';
import { formatCurrencyAdaptive, formatNumber, toNumber } from '@/lib/utils';

import { ActiveGoalsPanel } from '../components/active-goals-panel';
import { ActiveShiftsPanel } from '../components/active-shifts-panel';
import { AlertPanel } from '../components/alert-panel';
import { DailySalesChart } from '../components/daily-sales-chart';
import { DateRangePicker } from '../components/date-range-picker';
import { DispenserPerformancePanel } from '../components/dispenser-performance-panel';
import { FinancialSummaryCards } from '../components/financial-summary-cards';
import { FuelSalesChart } from '../components/fuel-sales-chart';
import { HourlyTrafficChart } from '../components/hourly-traffic-chart';
import { KpiCard } from '../components/kpi-card';
import { PaymentMethodsChart } from '../components/payment-methods-chart';
import { PriceHistoryChart } from '../components/price-history-chart';
import { TankStatusPanel } from '../components/tank-status-panel';
import { TopDebtorsPanel } from '../components/top-debtors-panel';
import {
  useActiveGoals,
  useActiveShifts,
  useCreditosCanjear,
  useDailySales,
  useDashboardAlerts,
  useDashboardSummary,
  useDispenserPerformance,
  useFinancialSummary,
  useFuelSales,
  useHourlyTraffic,
  usePaymentMethods,
  usePriceHistory,
  useTanks,
  useTopDebtors,
} from '../hooks/use-dashboard';
import { useDateRange } from '../hooks/use-date-range';

/**
 * Alto del área de dibujo de los gráficos SVG del dashboard.
 * Es mayor que los 240 por defecto porque estas tarjetas son anchas (dos
 * columnas) y con el alto por defecto la figura queda demasiado aplastada.
 */
const ALTURA_GRAFICO = 300;

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Buenos días';
  if (hour < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

/** Texto de la variación contra el período anterior. */
function variacionTexto(variacion: number): string {
  if (variacion > 0) return `↑ ${variacion.toFixed(2)}% vs período anterior`;
  if (variacion < 0) return `↓ ${Math.abs(variacion).toFixed(2)}% vs período anterior`;
  return 'Sin variación vs período anterior';
}

/** Tono del texto de variación (verde/rojo/neutro). */
function variacionTono(variacion: number): 'up' | 'down' | 'neutral' {
  if (variacion > 0) return 'up';
  if (variacion < 0) return 'down';
  return 'neutral';
}

/**
 * Dashboard ejecutivo.
 *
 * Réplica del dashboard del panel anterior para usuarios con el módulo
 * `dashboard`: alertas operativas, indicadores del período, estado de tanques,
 * ventas por combustible, variación de precios, tráfico por hora, rendimiento
 * por lado, turnos abiertos, dinero del período, deudores y metas activas.
 *
 * Los datos salen de PTS (`pts_pump_transactions`) y de las tablas de venta, así
 * que todo respeta el rango de fechas elegido; los tanques y los turnos son
 * siempre el estado actual.
 */
export function DashboardPage() {
  useDocumentTitle('Dashboard');

  const user = useAuthStore(selectUser);
  const canSeeMetrics = Boolean(user?.isAdmin) || (user?.roles.includes('dashboard') ?? false);

  const { preset, setPreset, custom, setCustom, range, aviso } = useDateRange('month');

  const summary = useDashboardSummary(range, canSeeMetrics);
  const dailySales = useDailySales(range, canSeeMetrics);
  const fuelSales = useFuelSales(range, canSeeMetrics);
  const paymentMethods = usePaymentMethods(range, canSeeMetrics);
  const hourlyTraffic = useHourlyTraffic(range, canSeeMetrics);
  const dispensers = useDispenserPerformance(range, canSeeMetrics);
  const prices = usePriceHistory(range, canSeeMetrics);
  const tanks = useTanks(canSeeMetrics);
  const alerts = useDashboardAlerts(canSeeMetrics);
  const financial = useFinancialSummary(range, canSeeMetrics);
  const creditos = useCreditosCanjear(range, canSeeMetrics);
  const debtors = useTopDebtors(canSeeMetrics);
  const activeShifts = useActiveShifts(canSeeMetrics);
  const goals = useActiveGoals(canSeeMetrics);

  const totalAmount = toNumber(summary.data?.total_monto);
  const totalGallons = toNumber(summary.data?.total_galones);
  const totalTransactions = toNumber(summary.data?.total_transacciones);
  const comparacion = summary.data?.comparacion;

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink-900">
            {getGreeting()}, {user?.firstName ?? user?.displayName ?? 'bienvenido'}
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            {user?.company
              ? `Resumen operativo de ${user.company}`
              : 'Resumen operativo de la estación de servicio'}
          </p>
        </div>

        {canSeeMetrics ? (
          <DateRangePicker
            preset={preset}
            onPresetChange={setPreset}
            custom={custom}
            onCustomChange={setCustom}
            range={range}
            aviso={aviso}
          />
        ) : null}
      </header>

      {summary.isError ? (
        <Alert variant="error" title="No se pudieron cargar los indicadores">
          No fue posible obtener el resumen de ventas. Verifica la conexión con el servidor e
          inténtalo nuevamente.
        </Alert>
      ) : null}

      {/* Alertas operativas */}
      {canSeeMetrics ? (
        <section aria-label="Alertas operativas" className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Alertas operativas
          </h2>
          <AlertPanel alerts={alerts.data} loading={alerts.isPending} />
        </section>
      ) : null}

      {/* Dinero del período */}
      {canSeeMetrics ? (
        <FinancialSummaryCards data={financial.data} loading={financial.isPending} />
      ) : null}

      {/* Indicadores */}
      {canSeeMetrics ? (
        <section aria-label="Indicadores del periodo" className="grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Ventas del periodo"
            value={formatCurrencyAdaptive(totalAmount)}
            hint={comparacion ? variacionTexto(comparacion.monto.variacion) : undefined}
            hintTone={comparacion ? variacionTono(comparacion.monto.variacion) : undefined}
            icon={Wallet}
            tone="brand"
            loading={summary.isPending}
          />
          <KpiCard
            label="Galones despachados"
            value={`${formatNumber(totalGallons)} gal`}
            hint={comparacion ? variacionTexto(comparacion.galones.variacion) : 'Combustibles líquidos'}
            hintTone={comparacion ? variacionTono(comparacion.galones.variacion) : undefined}
            icon={Droplets}
            tone="accent"
            loading={summary.isPending}
          />
          <KpiCard
            label="Transacciones"
            value={formatNumber(totalTransactions)}
            hint={comparacion ? variacionTexto(comparacion.transacciones.variacion) : 'Operaciones registradas'}
            hintTone={comparacion ? variacionTono(comparacion.transacciones.variacion) : undefined}
            icon={Receipt}
            tone="ink"
            loading={summary.isPending}
          />
          <KpiCard
            label="Créditos por canjear"
            value={formatCurrencyAdaptive(creditos.data?.monto ?? 0)}
            hint={`${formatNumber(creditos.data?.cantidad ?? 0)} ventas sin CPE`}
            icon={Ticket}
            tone="rose"
            loading={creditos.isPending}
          />
        </section>
      ) : (
        <Alert variant="info" title="Módulo de indicadores no habilitado">
          Tu usuario no tiene acceso al módulo de métricas. Solicítalo al administrador del sistema
          si necesitas consultar las ventas del periodo.
        </Alert>
      )}

      {/* Filas simétricas: todas las tarjetas de una fila comparten altura y cada
          panel distribuye su contenido (los resúmenes van como pie de tarjeta). */}
      <div className="grid gap-6 xl:grid-cols-3">
        {canSeeMetrics ? (
          <>
            <SectionCard
              className="xl:col-span-2"
              title="Ventas por día"
              description="Monto facturado y galones despachados por día"
            >
              <DailySalesChart
                data={dailySales.data}
                loading={dailySales.isPending}
                height={ALTURA_GRAFICO}
              />
            </SectionCard>

            <SectionCard title="Metas activas" description="Avance de las metas vigentes">
              <ActiveGoalsPanel data={goals.data} loading={goals.isPending} />
            </SectionCard>

            <SectionCard
              className="xl:col-span-2"
              title="Variación de precios"
              description="Precio promedio diario por combustible (PTS)"
            >
              <PriceHistoryChart
                data={prices.data}
                loading={prices.isPending}
                height={ALTURA_GRAFICO}
              />
            </SectionCard>

            <SectionCard title="Estado de tanques" description="Última medición registrada por PTS">
              <TankStatusPanel tanks={tanks.data} loading={tanks.isPending} />
            </SectionCard>

            <SectionCard
              className="xl:col-span-2"
              title="Ventas por combustible"
              description="Galones despachados y monto por producto"
            >
              <FuelSalesChart
                data={fuelSales.data}
                loading={fuelSales.isPending}
                height={ALTURA_GRAFICO}
              />
            </SectionCard>

            <SectionCard
              title="Métodos de pago"
              description="Cómo pagaron los clientes en el período"
            >
              <PaymentMethodsChart data={paymentMethods.data} loading={paymentMethods.isPending} />
            </SectionCard>

            <SectionCard
              className="xl:col-span-2"
              title="Tráfico por hora"
              description="Transacciones y monto facturado a lo largo del día"
            >
              <HourlyTrafficChart
                data={hourlyTraffic.data}
                loading={hourlyTraffic.isPending}
                height={ALTURA_GRAFICO}
              />
            </SectionCard>

            <SectionCard title="Principales deudores" description="Saldos pendientes de clientes">
              <TopDebtorsPanel data={debtors.data} loading={debtors.isPending} />
            </SectionCard>

            <SectionCard
              className="xl:col-span-2"
              title="Rendimiento por lado"
              description="Monto, galones y operaciones por lado y manguera, ordenado por monto"
            >
              <DispenserPerformancePanel data={dispensers.data} loading={dispensers.isPending} />
            </SectionCard>

            <SectionCard title="Turnos activos" description="Turnos abiertos en este momento">
              <ActiveShiftsPanel shifts={activeShifts.data} loading={activeShifts.isPending} />
            </SectionCard>
          </>
        ) : (
          <SectionCard
            className="xl:col-span-3"
            title="Indicadores no habilitados"
            description="Tu usuario no tiene acceso al módulo de métricas"
          >
            <p className="text-sm text-ink-500">
              Solicita el acceso al administrador del sistema si necesitas consultar las ventas,
              tanques y turnos del período.
            </p>
          </SectionCard>
        )}
      </div>
    </div>
  );
}
